export const apiSecurityModule = {
  id: "api-security-enterprise",
  title: "Enterprise API Security",
  description: "Implement comprehensive security measures for enterprise APIs including authentication, authorization, and threat protection.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Implement OAuth 2.0, JWT, and API key authentication for enterprise APIs
- Design role-based access control (RBAC) and attribute-based access control (ABAC) systems
- Secure API endpoints against common threats (OWASP API Security Top 10)
- Monitor and respond to security events in real-time

## Prerequisites
- Understanding of HTTP protocols and REST APIs
- Basic knowledge of cryptography and security principles
- Experience with authentication systems

## Overview
API security breaches cost enterprises an average of $4.24M per incident. This module teaches enterprise-grade security practices used by companies handling sensitive data at scale.

---

## Key Concepts

### Authentication Strategies

#### OAuth 2.0 Implementation
**Authorization Code Flow**: Most secure for web applications
**Client Credentials Flow**: For server-to-server communication
**Resource Owner Password Flow**: Legacy systems only

#### JWT Token Management
**Token Structure**: Header, payload, signature for stateless authentication
**Token Lifecycle**: Generation, validation, refresh, and revocation
**Security Considerations**: Secret management, expiration, claims validation

### Authorization Patterns

#### Role-Based Access Control (RBAC)
**Role Hierarchy**: Admin, Manager, User roles with inherited permissions
**Permission Matrix**: Actions mapped to resources for each role
**Dynamic Role Assignment**: Runtime role changes based on context

#### Attribute-Based Access Control (ABAC)
**Policy Engine**: Rule-based access decisions using attributes
**Context Awareness**: Time, location, device, and behavioral attributes
**Fine-Grained Control**: Resource-level permissions based on multiple attributes

### API Security Threats

#### OWASP API Security Top 10
**API1:2023 - Broken Object Level Authorization**: Direct object references without proper access checks
**API2:2023 - Broken Authentication**: Weak authentication mechanisms
**API3:2023 - Broken Object Property Level Authorization**: Mass assignment vulnerabilities
**API4:2023 - Unrestricted Resource Consumption**: Lack of rate limiting and resource controls

---

## Practical Examples

### Example 1: OAuth 2.0 Implementation
Secure API access using industry-standard OAuth 2.0 flow.

**Authorization Server Setup**:
Configure OAuth provider with proper client registration, scope management, and token lifecycle.

**Resource Server Protection**:
Implement middleware that validates access tokens, checks scopes, and handles authorization failures gracefully.

### Example 2: API Rate Limiting
Protect APIs from abuse and ensure fair resource usage.

**Rate Limiting Strategy**:
- Token bucket algorithm for burst handling
- Sliding window for accurate rate measurement
- Distributed rate limiting using Redis
- Custom limits per client tier (Basic, Premium, Enterprise)

**Implementation Patterns**:
Apply rate limits at multiple levels: global, per-user, per-endpoint, and per-resource.

### Example 3: Input Validation and Sanitization
Prevent injection attacks and data corruption.

**Schema Validation**:
Use JSON Schema or similar tools to validate request payloads against predefined schemas.

**SQL Injection Prevention**:
Implement parameterized queries and input sanitization for database operations.

---

## Hands-On Lab

### Lab: Secure User Management API
Build a complete user management API with enterprise-grade security.

**Requirements**:
1. OAuth 2.0 authentication with refresh tokens
2. Role-based authorization (Admin, Manager, User)
3. Rate limiting (100 requests per minute per user)
4. Input validation and sanitization
5. Audit logging for security events
6. API versioning with security headers

**Security Headers**:
Implement security headers (CORS, CSP, HSTS) and API versioning.

**Monitoring Integration**:
Set up security event logging and real-time threat detection.

---

## Assessment

### Security Checklist
- [ ] Authentication mechanism implemented correctly
- [ ] Authorization rules enforced at endpoint level
- [ ] Rate limiting configured for all endpoints
- [ ] Input validation prevents injection attacks
- [ ] Security headers properly configured
- [ ] Audit logging captures security events
- [ ] Token management follows best practices
- [ ] Error responses don't leak sensitive information

### Common Pitfalls
**Insufficient Authorization**: Checking authentication but not authorization
**Token Leakage**: Exposing tokens in logs or error messages
**Missing Rate Limits**: Allowing unlimited API calls
**Weak Validation**: Trusting client-side input without server validation

This module ensures your APIs meet enterprise security standards and can withstand real-world threats.
`,
  duration: 75,
  codeExamples: [
    {
      title: "OAuth 2.0 Authentication Middleware",
      language: "javascript",
      code: `// OAuth 2.0 token validation middleware
const jwt = require('jsonwebtoken');
const jwksClient = require('jwks-rsa');

const client = jwksClient({
  jwksUri: process.env.OAUTH_JWKS_URI,
  cache: true,
  cacheMaxAge: 86400000, // 24 hours
  rateLimit: true
});

const getSigningKey = (header, callback) => {
  client.getSigningKey(header.kid, (err, key) => {
    const signingKey = key.publicKey || key.rsaPublicKey;
    callback(null, signingKey);
  });
};

const authenticateToken = async (req, res, next) => {
  const authHeader = req.headers['authorization'];
  const token = authHeader && authHeader.split(' ')[1];

  if (!token) {
    return res.status(401).json({ 
      error: 'Access token required',
      code: 'MISSING_TOKEN'
    });
  }

  jwt.verify(token, getSigningKey, {
    audience: process.env.OAUTH_AUDIENCE,
    issuer: process.env.OAUTH_ISSUER,
    algorithms: ['RS256']
  }, (err, user) => {
    if (err) {
      return res.status(403).json({ 
        error: 'Invalid or expired token',
        code: 'INVALID_TOKEN'
      });
    }
    
    req.user = user;
    next();
  });
};

module.exports = { authenticateToken };`
    },
    {
      title: "Role-Based Authorization",
      language: "javascript", 
      code: `// RBAC middleware implementation
const authorize = (requiredRoles = []) => {
  return (req, res, next) => {
    if (!req.user) {
      return res.status(401).json({ 
        error: 'Authentication required',
        code: 'UNAUTHENTICATED'
      });
    }

    const userRoles = req.user.roles || [];
    const hasRequiredRole = requiredRoles.some(role => 
      userRoles.includes(role)
    );

    if (requiredRoles.length && !hasRequiredRole) {
      return res.status(403).json({ 
        error: 'Insufficient permissions',
        code: 'INSUFFICIENT_PERMISSIONS',
        required: requiredRoles,
        current: userRoles
      });
    }

    next();
  };
};

// Usage examples
app.get('/api/users', 
  authenticateToken, 
  authorize(['admin', 'manager']), 
  getUsersController
);

app.delete('/api/users/:id', 
  authenticateToken, 
  authorize(['admin']), 
  deleteUserController
);`
    }
  ],
  practice: {
    title: "Implement API Security Layer",
    description: "Add comprehensive security to an existing user management API.",
    starter: `// Unsecured API endpoints
app.get('/api/users', (req, res) => {
  // No authentication or authorization
  const users = database.getAllUsers();
  res.json(users);
});

app.post('/api/users', (req, res) => {
  // No input validation
  const user = database.createUser(req.body);
  res.json(user);
});`,
    solution: `// Secured API with comprehensive protection
const rateLimit = require('express-rate-limit');
const helmet = require('helmet');
const { body, validationResult } = require('express-validator');

// Security middleware
app.use(helmet());
app.use(rateLimit({
  windowMs: 60 * 1000, // 1 minute
  max: 100, // limit each IP to 100 requests per windowMs
  message: 'Too many requests, please try again later'
}));

// Secured user listing with authorization
app.get('/api/users', 
  authenticateToken,
  authorize(['admin', 'manager']),
  async (req, res) => {
    try {
      const users = await database.getAllUsers();
      // Remove sensitive fields
      const safeUsers = users.map(user => ({
        id: user.id,
        username: user.username,
        email: user.email,
        role: user.role,
        createdAt: user.createdAt
      }));
      res.json(safeUsers);
    } catch (error) {
      res.status(500).json({ error: 'Internal server error' });
    }
  }
);

// Secured user creation with validation
app.post('/api/users',
  authenticateToken,
  authorize(['admin']),
  [
    body('username').isLength({ min: 3, max: 20 }).isAlphanumeric(),
    body('email').isEmail().normalizeEmail(),
    body('password').isLength({ min: 8 }).matches(/^(?=.*[A-Za-z])(?=.*\\d)(?=.*[@$!%*#?&])/),
    body('role').isIn(['user', 'manager', 'admin'])
  ],
  async (req, res) => {
    const errors = validationResult(req);
    if (!errors.isEmpty()) {
      return res.status(400).json({ 
        error: 'Validation failed',
        details: errors.array()
      });
    }

    try {
      const user = await database.createUser(req.body);
      // Log security event
      auditLog.log('USER_CREATED', req.user.id, user.id);
      res.status(201).json({ 
        id: user.id, 
        username: user.username, 
        email: user.email 
      });
    } catch (error) {
      res.status(500).json({ error: 'Failed to create user' });
    }
  }
);`
  },
  quiz: {
    question: "Which OAuth 2.0 flow is most appropriate for server-to-server API communication?",
    options: [
      "Authorization Code Flow",
      "Implicit Flow", 
      "Client Credentials Flow",
      "Resource Owner Password Flow"
    ],
    correctAnswer: 2,
    explanation: "Client Credentials Flow is designed for server-to-server communication where no user interaction is required, making it ideal for API-to-API authentication."
  }
};